import { Vue, Component, Ref, Prop } from 'vue-property-decorator'
import { VForm } from '@/types/vuetify/VForm'
import isEmpty from '@/helpers/isEmpty'
import validateEmail from '@/helpers/validateEmail'
import checkURL from '@/helpers/checkURL'
import messages from '@/config/messages'
import { VTextArea } from '@/types/vuetify/VTextArea'
import normalizeText from '@/helpers/normalizeText'
import cloneObject from '@/helpers/cloneObject'
import { FormField } from '../../ui'
import { getAutoRel, getRoutePlatformOrigin } from '../../helpers'
import {
  ELinkRel,
  ELinkType,
  EProtocol,
  ETabCode,
  ETarget,
  ILinkFormProps,
  TLinkFormData,
} from './types'
import { AVAILABLE_PROTOCOLS } from '../../config'

@Component({
  inheritAttrs: false,
  components: {
    FormField,
  },
})
export default class LinkForm extends Vue {
  @Ref('form')
  private readonly formRef?: VForm

  @Ref('hrefField')
  private readonly hrefFieldRef?: VTextArea

  @Prop({ type: Object, required: true })
  private readonly data!: ILinkFormProps['data']

  @Prop({ type: Function, required: true })
  private checkAllowedIndexingSettings!: ILinkFormProps['checkAllowedIndexingSettings']

  @Prop({ type: Function, required: true })
  private checkDisabledIndexingSettings!: ILinkFormProps['checkDisabledIndexingSettings']

  private activeTabCode = ETabCode.INFO
  private formData: TLinkFormData | null = null
  private isValidForm = false
  private isRelChangedManually = false

  private get linkTypeSelectMaxHeight() {
    if (
      this.formData?.linkType === 'link-anchor' ||
      this.formData?.linkType === 'phone'
    ) {
      return 180
    }

    if (
      !this.isAllowedIndexingSettings &&
      this.formData?.linkType !== 'email'
    ) {
      return 180
    }

    return 225
  }

  private get isAllowedIndexingSettings() {
    return this.checkAllowedIndexingSettings()
  }

  private get isDisabledIndexingSettings() {
    return this.checkDisabledIndexingSettings()
  }

  private get hasChanges() {
    if (!this.formData) {
      return false
    }

    const combinedData = {
      ...this.data,
      ...this.formData,
    }

    const initialData: Record<string, unknown> = this.data
    const currentData: Record<string, unknown> = this.formData

    for (const key in combinedData) {
      if (!Object.hasOwn(combinedData, key)) {
        continue
      }

      if (initialData[key] !== currentData[key]) {
        return true
      }
    }

    return false
  }

  private get isExistingLink() {
    return this.data.linkType === ELinkType.LINK && !isEmpty(this.data.href)
  }

  private get autoRel() {
    if (
      this.formData?.linkType !== ELinkType.LINK ||
      this.isRelChangedManually
    ) {
      return
    }

    if (this.isExistingLink && !this.isDisabledIndexingSettings) {
      return
    }

    return getAutoRel(this.formData.href)
  }

  private get indexingSettingsHint() {
    const hint =
      'Режим индексации определяется автоматически: для внутренних ссылок — dofollow, для внешних — nofollow.'

    if (this.isDisabledIndexingSettings) {
      return hint
    }

    return `${hint}<br />В случае, если вы понимаете, что нужен другой режим, его можно выбрать ниже:`
  }

  private get platformOrigin() {
    return getRoutePlatformOrigin(this.$route.path)
  }

  private get fieldRules() {
    return {
      required: (v: string) => !isEmpty(v) || messages.errors.requiredField,
      url: (v: string) => checkURL(v) || messages.errors.invalidUrl,
      email: (v: string) => !!validateEmail(v) || messages.errors.invalidEmail,
    }
  }

  private get anchorItems() {
    const anchorItems: {
      value: string
      text: string
    }[] = []

    document.body.querySelectorAll('a[name][id]').forEach((anchorEl) => {
      const name = anchorEl.getAttribute('name')

      if (!name || !anchorEl.id) {
        return
      }

      anchorItems.push({
        value: anchorEl.id,
        text: name,
      })
    })

    return anchorItems
  }

  private get linkItems() {
    return [
      {
        text: 'Ссылка',
        value: ELinkType.LINK,
      },
      {
        text: 'Ссылка на якорь в тексте',
        value: ELinkType.LINK_ANCHOR,
      },
      {
        text: 'Email',
        value: ELinkType.EMAIL,
      },
      {
        text: 'Phone',
        value: ELinkType.PHONE,
      },
    ]
  }

  private get targetItems() {
    return [
      {
        text: '<не указано>',
        value: ETarget.NOT_SET,
      },
      {
        text: 'Новое окно(_blank)',
        value: ETarget.BLANK,
      },
      {
        text: 'Главное окно(_top)',
        value: ETarget.TOP,
      },
      {
        text: 'Текущее окно(_self)',
        value: ETarget.SELF,
      },
      {
        text: 'Родительское окно(_parent)',
        value: ETarget.PARENT,
      },
    ]
  }

  private get protocolItems() {
    return [
      {
        text: 'http://',
        value: EProtocol.HTTP,
      },
      {
        text: 'https://',
        value: EProtocol.HTTPS,
      },
    ]
  }

  private get tabItems() {
    return [
      {
        name: 'Информация о ссылке',
        code: ETabCode.INFO,
        handleClick: () => {
          this.activeTabCode = ETabCode.INFO
        },
      },
      {
        enabled: this.formData?.linkType === ELinkType.LINK,
        name: 'Цель',
        code: ETabCode.TARGET,
        handleClick: () => {
          this.activeTabCode = ETabCode.TARGET
        },
      },
    ].filter(({ enabled = true }) => enabled)
  }

  private get relItems() {
    return [
      {
        text: 'nofollow (закрыта для индексации)',
        code: 'nofollow',
        value: ELinkRel.NOFOLLOW,
      },
      {
        text: 'dofollow (открыта для индексации)',
        code: 'dofollow',
        value: null,
      },
      {
        code: 'sponsored_nofollow',
        text: 'sponsored (реклама и проч.)',
        value: ELinkRel.SPONSORED_NOFOLLOW,
      },
    ]
  }

  private handleChangeRel() {
    this.isRelChangedManually = true
  }

  private handleCancel() {
    this.$emit('cancel')
  }

  private async handleSubmitForm() {
    await this.$nextTick()

    if (!this.formRef || !this.formData) {
      return
    }

    const isValidForm = this.formRef.validate()

    if (!isValidForm) {
      for (const inputInstance of this.formRef.inputs) {
        if (inputInstance.valid) {
          continue
        }

        const invalidTabCode = inputInstance.$el
          .closest('.js-link-form-tab')
          ?.getAttribute('data-tab-code')

        if (
          !invalidTabCode ||
          !this.tabItems.some((tabItem) => tabItem.code === invalidTabCode)
        ) {
          continue
        }

        this.activeTabCode = invalidTabCode as ETabCode
      }

      return
    }

    const formData = cloneObject(this.formData)

    formData.text = normalizeText(this.formData.text)

    switch (formData.linkType) {
      case ELinkType.PHONE:
        formData.phoneNumber = formData.phoneNumber.replace(/\s+/g, '')
        break

      case ELinkType.LINK:
        formData.href = new URL(formData.href).toString()

        if (this.isDisabledIndexingSettings) {
          formData.rel = getAutoRel(formData.href)
        }

        break

      default:
        break
    }

    this.$emit('submit', formData)
  }

  private created() {
    this.formData = cloneObject(this.data)

    this.$watch(
      () => {
        return this.formData?.linkType || ELinkType.LINK
      },
      (linkType) => {
        const text = this.formData?.text || ''

        this.isRelChangedManually = false

        switch (linkType) {
          case ELinkType.LINK:
            this.formData = {
              text,
              href: '',
              target: ETarget.NOT_SET,
              protocol: EProtocol.HTTPS,
              linkType: ELinkType.LINK,
              rel: ELinkRel.NOFOLLOW,
            }

            break

          case ELinkType.EMAIL:
            this.formData = {
              text,
              subject: '',
              body: '',
              email: '',
              linkType: ELinkType.EMAIL,
            }

            break

          case ELinkType.LINK_ANCHOR:
            this.formData = {
              text,
              anchorId: '',
              linkType: ELinkType.LINK_ANCHOR,
            }

            break

          case ELinkType.PHONE:
            this.formData = {
              text,
              phoneNumber: '',
              linkType: ELinkType.PHONE,
            }

            break

          default:
            break
        }
      }
    )

    this.$watch(
      () => {
        return this.formData?.linkType === ELinkType.LINK &&
          checkURL(this.formData.href)
          ? new URL(this.formData.href)
          : null
      },
      (url) => {
        if (this.formData?.linkType !== ELinkType.LINK) {
          return
        }

        const protocol = url?.protocol as EProtocol | undefined

        if (protocol && AVAILABLE_PROTOCOLS.includes(protocol)) {
          this.formData.protocol = protocol
        } else if (url?.protocol) {
          url.protocol = EProtocol.HTTPS

          this.formData.href = url.toString()
        }

        if (
          url &&
          this.data.linkType === ELinkType.LINK &&
          this.data.href === this.formData.href
        ) {
          this.formData.target = this.data.target

          if (!this.isRelChangedManually) {
            this.formData.rel = this.data.rel
          }
        } else {
          if (
            this.platformOrigin &&
            url?.origin &&
            this.formData.target !== ETarget.SELF
          ) {
            this.formData.target =
              url.origin === this.platformOrigin
                ? ETarget.NOT_SET
                : ETarget.BLANK
          }

          if (this.autoRel !== undefined) {
            this.formData.rel = this.autoRel
          }
        }
      }
    )

    this.$watch(
      () => {
        return this.formData?.linkType === ELinkType.LINK
          ? this.formData.protocol
          : null
      },
      (newProtocol) => {
        if (!newProtocol || this.formData?.linkType !== ELinkType.LINK) {
          return
        }

        const url = checkURL(this.formData.href)
          ? new URL(this.formData.href)
          : null

        if (url) {
          this.hrefFieldRef?.blur()
          url.protocol = newProtocol

          this.formData.href = url.toString()
        }
      }
    )
  }
}
